import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Hls from 'hls.js';
import axios from 'axios';
import './CameraInfo.css';

function CameraInfo({ camera, onClose, onNavigate, totalResults }) {
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(false);
  const [streamError, setStreamError] = useState('');
  const videoRef = useRef(null);
  const playerWrapRef = useRef(null);

  const loadStream = useCallback(async () => {
    setLoading(true);
    setStreamError('');

    try {
      const response = await axios.get(`/api/streams/${encodeURIComponent(camera.id)}`);
      if (!response.data?.stream) {
        throw new Error('Stream not available');
      }
      setStream(response.data.stream);
    } catch (error) {
      setStreamError(error.response?.data?.error || 'Stream could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [camera.id]);

  useEffect(() => {
    setStream(null);
    setStreamError('');

    if (camera?.hasStream) {
      loadStream();
    }
  }, [camera, loadStream]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && onClose) {
        event.preventDefault();
        onClose();
      } else if (event.key === 'ArrowLeft' && onNavigate && totalResults > 1) {
        event.preventDefault();
        onNavigate(-1);
      } else if (event.key === 'ArrowRight' && onNavigate && totalResults > 1) {
        event.preventDefault();
        onNavigate(1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onNavigate, totalResults]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !stream?.url || stream.type !== 'hls') {
      return undefined;
    }

    let hls;

    if (Hls.isSupported()) {
      hls = new Hls({
        lowLatencyMode: true,
        enableWorker: true,
        backBufferLength: 30
      });
      hls.loadSource(stream.url);
      hls.attachMedia(video);
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = stream.url;
    }

    return () => {
      if (hls) {
        hls.destroy();
      }
    };
  }, [stream]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !stream?.url || stream.type !== 'video') {
      return;
    }

    video.src = stream.url;
  }, [stream]);

  const locationLabel = [camera.city, camera.region, camera.country].filter(Boolean).join(', ');
  const availability = stream?.availabilityStatus || camera.availabilityStatus || (camera.isActive === false ? 'offline' : camera.hasStream ? 'online' : 'unknown');
  const streamTypeLabel = useMemo(() => {
    if (!stream?.type) {
      return '';
    }
    return stream.type.toUpperCase();
  }, [stream]);
  const coordinates = Number.isFinite(camera.latitude) && Number.isFinite(camera.longitude)
    ? `${camera.latitude.toFixed(4)}°, ${camera.longitude.toFixed(4)}°`
    : '';

  const technicalItems = [
    ['IP', camera.ipAddress],
    ['Host', camera.host],
    ['Timezone', camera.timezone],
    ['ISP', camera.isp],
    ['Organization', camera.organization],
    ['AS', camera.asname],
    ['Continent', camera.continent],
    ['District', camera.district],
    ['Postal code', camera.postalCode],
    ['Reverse', camera.reverse],
    ['Registrar', camera.registrar],
    ['WHOIS name', camera.whoisName],
    ['WHOIS handle', camera.whoisHandle],
    ['TLS issuer', camera.tlsIssuer],
    ['TLS subject', camera.tlsSubject],
    ['TLS valid from', camera.tlsValidFrom],
    ['TLS valid to', camera.tlsValidTo],
    ['HTTP status', camera.httpStatus],
    ['Server', camera.serverHeader],
    ['Proxy', camera.isProxy ? 'Yes' : ''],
    ['Hosting', camera.isHosting ? 'Yes' : ''],
    ['Mobile', camera.isMobile ? 'Yes' : '']
  ].filter(([, value]) => Boolean(value));

  const openFullscreen = async () => {
    const target = stream?.type === 'video' || stream?.type === 'hls' ? videoRef.current : playerWrapRef.current;
    if (!target?.requestFullscreen) {
      return;
    }

    try {
      await target.requestFullscreen();
    } catch (error) {
    }
  };

  const renderStreamPlayer = () => {
    if (!stream?.url) {
      return null;
    }

    if (stream.type === 'hls' || stream.type === 'video') {
      return (
        <video
          ref={videoRef}
          className="stream-player"
          controls
          autoPlay
          playsInline
          muted
          poster={stream.poster || camera.image || undefined}
        />
      );
    }

    if (stream.type === 'mjpeg' || stream.type === 'image') {
      return <img src={stream.url} alt={camera.title} className="stream-player stream-image" />;
    }

    return (
      <iframe
        src={stream.url}
        title={camera.title}
        className="stream-player stream-frame"
        allow="autoplay; fullscreen"
      />
    );
  };

  return (
    <div className="camera-info-overlay">
      <div className="camera-info-panel">
        <button className="close-button" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <div className="info-header">
          {camera.image ? (
            <img src={camera.image} alt={camera.title} className="camera-thumbnail" />
          ) : (
            <div className="camera-thumbnail camera-thumbnail-fallback" />
          )}
          <div className="info-title-section">
            <h2>{camera.title}</h2>
            <div className="title-badges">
              {camera.provider && <p className="provider-badge">{camera.provider.toUpperCase()}</p>}
              {availability === 'offline' && <p className="provider-badge provider-badge-offline">OFFLINE</p>}
            </div>
          </div>
        </div>

        <div className="info-content">
          {(stream || loading || streamError || camera.hasStream || camera.image) && (
            <div className="info-section">
              <div className="section-headline">
                <h3>Preview</h3>
                {stream?.url && (
                  <button type="button" className="mini-action" onClick={openFullscreen}>
                    Fullscreen
                  </button>
                )}
              </div>
              {streamTypeLabel && <p className="stream-url">{streamTypeLabel}</p>}
              {(stream || camera.image) && (
                <div className="player-wrap" ref={playerWrapRef}>
                  {stream ? renderStreamPlayer() : <img src={camera.image} alt={camera.title} className="stream-player stream-image" />}
                </div>
              )}
              {loading && <p className="loading">Loading stream</p>}
              {streamError && <p className="error">{streamError}</p>}
            </div>
          )}

          <div className="info-section">
            <h3>Position</h3>
            {coordinates && <p className="coordinates">{coordinates}</p>}
            {locationLabel && <p className="country">{locationLabel}</p>}
          </div>

          {camera.description && (
            <div className="info-section">
              <h3>Info</h3>
              <p>{camera.description}</p>
            </div>
          )}

          <div className="info-section">
            <h3>Status</h3>
            <div className="status-badges">
              <span className={`status-badge ${camera.isActive === false ? 'inactive' : 'active'}`}>
                {camera.isActive === false ? '● Inactive' : '● Active'}
              </span>
              {camera.hasStream && <span className="status-badge has-stream">● Live</span>}
              {availability === 'offline' && <span className="status-badge inactive">● Offline</span>}
            </div>
          </div>

          {(camera.region || camera.city || camera.county || camera.route || camera.direction) && (
            <div className="info-section info-grid">
              {camera.city && <div><h3>City</h3><p>{camera.city}</p></div>}
              {camera.region && <div><h3>Region</h3><p>{camera.region}</p></div>}
              {camera.county && <div><h3>District</h3><p>{camera.county}</p></div>}
              {camera.route && <div><h3>Route</h3><p>{camera.route}</p></div>}
              {camera.direction && <div><h3>Direction</h3><p>{camera.direction}</p></div>}
            </div>
          )}

          {technicalItems.length > 0 && (
            <div className="info-section info-grid">
              {technicalItems.map(([label, value]) => (
                <div key={label}>
                  <h3>{label}</h3>
                  <p>{value}</p>
                </div>
              ))}
            </div>
          )}

          {camera.hasStream && !stream && !loading && (
            <button className="load-stream-button" onClick={loadStream}>
              Load stream
            </button>
          )}

          {stream?.url && (
            <a href={stream.url} target="_blank" rel="noopener noreferrer" className="stream-link">
              Open stream
            </a>
          )}

          {camera.url && (
            <a href={camera.url} target="_blank" rel="noopener noreferrer" className="view-source-button">
              Open source
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default CameraInfo;
